import { sendFile } from './construct/file'
import { crccs } from './crccs'
import { splitEvery } from 'ramda'
import { match, P } from 'ts-pattern'
import * as cmd from './construct/command-constants'
import { create, QRCodeOptions } from 'qrcode'
interface SplitJavascriptOptions {
    readonly file: {
        readonly name: string
        readonly contents: string
    }
    readonly split: {
        readonly maxSize?: number
        readonly numberBarcodes?: number
    }
}

/**
 * Splits a JavaScript file into multiple parts based on the specified split options.
 *
 * @param options - The split options.
 * @returns An object containing the split parts and the combiner script.
 */
export const splitJavascript = ({
    file: { name: fileName, contents: source },
    split,
}: SplitJavascriptOptions) => {
    if (source === '') throw new Error('Source is empty')
    if (fileName === '') throw new Error('File Name is empty')

    const fileSize = source.length

    const partSize = match(split)
        .returnType<number>()

        .with(
            { maxSize: P.number, numberBarcodes: P.number },
            ({ maxSize, numberBarcodes }) => {
                if (fileSize / numberBarcodes > maxSize)
                    throw new Error(
                        `Max Size is too small for ${numberBarcodes} barcodes. Need at least ${Math.ceil(
                            fileSize / numberBarcodes
                        )}`
                    )

                return maxSize
            }
        )
        .with({ maxSize: P.select(P.number) }, (maxSize) => maxSize)

        .with({ numberBarcodes: P.select(P.number) }, (numberBarcodes) =>
            Math.ceil(fileSize / numberBarcodes)
        )

        .with(P._, () => {
            throw new Error('Max Size or Number of Barcodes are required')
        })

        .exhaustive()

    if (partSize < 1)
        throw new Error(
            'Max Size and Number of Barcodes must be greater than 0'
        )

    const numberBarcodes = Math.ceil(fileSize / partSize)

    // if (numberBarcodes > 20)
    //     throw new Error('Number of Barcodes must be less than 20')

    const parts: string[] = splitEvery(partSize, source).map((part, idx) =>
        crccs(sendFile(`${fileName}.part${idx + 1}of${numberBarcodes}`, part))
    )

    const combinerScriptSource = `include(".cra.js");var name="${fileName}",script="combinerScript.js";throwError=function(r){throw reader.indicateError(),storage.erase(script),r};var parseStr=name+".part[0-9]+of[0-9]+",file=storage.findFirst(parseStr),numParts=parseInt(file.substring(file.lastIndexOf("of")+2));NaN==numParts&&throwError("Expected File: "+name+".partXofY"),storage.write(name,"");for(var part=1;part<=numParts;)wdt_pet(),partItr=parseInt(file.substring(file.lastIndexOf(".")+5,file.lastIndexOf("of"))),partItr==part?(storage.append(name,storage.read(file)),storage.erase(file),part++,file=storage.findFirst(parseStr)):NaN==partItr||null==file?throwError("Cannot Find: "+part+" of "+numParts+" Itr: "+partItr):file=storage.findNext();storage.erase(script);`

    const combine = crccs([
        ...sendFile('combinerScript.js', combinerScriptSource),
        `${cmd.execute}"combinerScript.js"`,
    ])

    return { parts, combine } as const
}

export const barcodesForSize = (
    qrCode: QRCodeOptions,
    jsOption: SplitJavascriptOptions
) => {
    let isValid = Infinity
    try {
        const split = splitJavascript(jsOption)
        split.parts.forEach((part) => create(part, qrCode))
        isValid = split.parts.length
    } catch (e) {
        if (
            !(
                e instanceof Error &&
                e.message.includes(
                    'The amount of data is too big to be stored in a QR Code'
                )
            )
        )
            throw e
    }

    return isValid
}

export const maxSplitJavaScript = (
    qrCode: QRCodeOptions,
    file: SplitJavascriptOptions['file']
) => {
    const MAX_SIZE = file.contents.length + file.name.length + 10000
    let begin = 1
    let end = MAX_SIZE
    let minBarcodes = Infinity
    // want the smallest number that doesn't increase the number of barcodes

    while (begin < end) {
        const mid = Math.floor((begin + end) / 2)
        const barcodes = barcodesForSize(qrCode, {
            file,
            split: { maxSize: mid },
        })
        if (barcodes <= minBarcodes) {
            minBarcodes = barcodes
            end = mid
        } else {
            begin = mid + 1
        }
    }

    return [end, minBarcodes] as const
}
